import React from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import styled from 'styled-components';
import Divider from '~/components/divider';
import { formats } from '~/helper';
import Modal from '~/components/modal';
import { MdAdd, MdDelete } from 'react-icons/md';
import { Autocomplete, InputGroup, InputMask, InputDate, Select } from '~/components/form';
import FileImageCrop from '~/components/form/file-image-crop';
import { IconButton } from '~/components/crud/styles';
import ImageAuth from '~/components/image';
import { MASK_CPF, MASK_PHONE } from '~constants'
import { isValidCpf } from '~/helper';
import { Row, RowAction, ImageContainer } from '~/views/events/matchday/styles';

const Container = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;

  span {
    color: #d61305;
    margin: 0 0 10px;
  }

  img {
    max-width: 100px;
    max-height: 100px;
    align-self: center;
    display: flex;
  }
`;

const EmployeeSchema = Yup.object().shape({
  employee: Yup.object().shape({
    role: Yup.mixed().test('', 'Informe a função', function(value) {
      return !_.isEmpty(value);
    }),
    name: Yup.string().required('Informe o nome'),
    identify: Yup.mixed().test('', 'Informe um CPF válido', function(value) {
      return value && isValidCpf(value);
    })
  })
});

const getPhotoUrl = (object) => {
  if (_.get(object, 'photo')) {
    return `/persons/photo/${_.get(object, 'photo')}`;
  }
  return _.get(object, 'photoUrl') ?  `/persons/photo-remote/?filename=${encodeURIComponent(_.get(object, 'photoUrl'))}` : '';
};

function EmployeeModal({ title, previewMode, data, isOpen, loading, onListSuppliers, closeModal, handleOnSubmit, handleCancelling, onListPersons, onListRoles }) {

  return (
    <Formik
      enableReinitialize={true}
      initialValues={data}
      validationSchema={EmployeeSchema}
      onSubmit={handleOnSubmit}
      >
      {({ values, errors, isValid, touched, setFieldValue, handleSubmit, handleReset }) => (
        <Modal
          width="600px"
          height="600px"
          open={isOpen}
          hide={closeModal}
          title={title}
          actions={[
            {
              label: 'Cancelar',
              action: () => { handleReset() ; closeModal() },
              disabled: loading
            },
            {
              label: loading ? 'Aguarde...' : 'Salvar',
              action: () => { handleOnSubmit(values); handleReset() },
              type: 'submit',
              primary: true,
              disabled: !isValid || loading || !_.get(values, 'employee')
            }
          ]}
          >
          <Container>
            <Autocomplete
              name='supplier'
              label="Fornecedor"
              minLength={1}
              keyField='id'
              value={values.supplier}
              disabled={previewMode}
              required={true}
              valueFormat={(row) => `${formats.cnpj(row.identify) || row.identify || ''} - ${row.name}`}
              loadData={onListSuppliers}
              emptyText={'Pesquise um fornecedor'}
              tipText={'Digite... '}
              loadingText={'Carregando...'}
              notFoundText={'Não encontrado'}
              />

            <RowAction>
              <Autocomplete
                name='employee'
                label="Colaborador"
                minLength={1}
                keyField='id'
                required={true}
                value={values.employee || {}}
                disabled={previewMode || !values.supplier}
                valueFormat={(row) => `${row.name || ''}`}
                loadData={(term, callback) => onListPersons(term, values.supplier, callback)}
                emptyText={'Pesquise um colaborador'}
                tipText={'Digite... '}
                loadingText={'Carregando...'}
                notFoundText={'Não encontrado'}
                />

              <IconButton
                title="Cadastrar novo"
                type="button"
                disabled={previewMode || !values.supplier}
                onClick={() => setFieldValue('employee', { companyId: _.get(values, 'supplier.id'), accountId: _.get(values, 'supplier.accountId') })}>
                <MdAdd />
              </IconButton>
            </RowAction>

            { values.employee &&
              <>
                <Divider label="Dados do colaborador" />

              <section>
                <Autocomplete
                  name='employee.role'
                  label="Função"
                  minLength={1}
                  keyField='id'
                  required={true}
                  value={_.get(values, 'employee.role')}
                  disabled={previewMode}
                  valueFormat={(row) => `${row.name}`}
                  loadData={onListRoles}
                  emptyText={'Pesquise uma função'}
                  tipText={'Digite... '}
                  loadingText={'Carregando...'}
                  notFoundText={'Não encontrada'}
                  />

                <InputGroup
                  type="text"
                  name="employee.name"
                  label="Nome"
                  required={true}
                  maxLength={100}
                  disabled={previewMode}
                  hasError={_.get(errors, 'employee.name') && _.get(touched, 'employee.name')} />

                <InputGroup
                  type="email"
                  name="employee.email"
                  label="Email"
                  maxLength={100}
                  disabled={previewMode}
                  hasError={_.get(errors, 'employee.email') && _.get(touched, 'employee.email')} />

                <Row span={2}>
                  <InputMask
                    disabled={previewMode}
                    mask={MASK_CPF}
                    name="employee.identify"
                    label={'CPF/Nº registro'}
                    required={true}
                    hasError={_.get(errors, 'employee.identify') && _.get(touched, 'employee.identify')} />

                  <InputMask
                    type="text"
                    name="employee.identityDoc"
                    label="RG"
                    disabled={previewMode}
                    mask={{ numericOnly: true, blocks: [ 20 ] }}
                    hasError={_.get(errors, 'employee.identityDoc') && _.get(touched, 'employee.identityDoc')} />
                </Row>

                <Row span={2}>
                  <InputDate
                    name="employee.birthdate"
                    label="Data de nascimento"
                    disabled={previewMode}
                    hasError={_.get(errors, 'employee.birthdate') && _.get(touched, 'employee.birthdate')} />

                  <InputMask
                    name="employee.phone"
                    label="Telefone"
                    mask={MASK_PHONE}
                    disabled={previewMode}
                    value={_.get(values, 'employee.phone')}
                    hasError={_.get(errors, 'employee.phone') && _.get(touched, 'employee.phone')} />
                </Row>

                <Row span={2}>
                  <InputGroup
                    type="text"
                    name="employee.registration"
                    label="Matrícula"
                    maxLength={50}
                    disabled={previewMode}
                    hasError={_.get(errors, 'employee.registration') && _.get(touched, 'employee.registration')} />

                  <Select
                    name="employee.linkType"
                    label="Tipo de vínculo"
                    options={{ values: [
                      { value: 'terceirizado', label: 'Terceirizado' },
                      { value: 'horista', label: 'Horista' },
                      { value: 'mensalista', label: 'Mensalista' },
                    ]}}
                    hasError={_.get(errors, 'employee.linkType') && _.get(touched, 'employee.linkType')} />
                </Row>

                <Row span={2}>
                  <InputGroup
                    type="text"
                    name="employee.covidResult"
                    label="Resultado do exame da COVID"
                    disabled={previewMode}
                    maxLength={50}
                    hasError={_.get(errors, 'employee.covidResult') && _.get(touched, 'employee.covidResult')} />

                  <InputDate
                    name="employee.covidDate"
                    label="Data do exame da COVID"
                    disabled={previewMode}
                    hasError={_.get(errors, 'employee.covidDate') && _.get(touched, 'employee.covidDate')} />
                </Row>

                <ImageContainer>
                  <h4>
                    <span>Foto</span>
                    {(_.get(values, 'employee.photo') || _.get(values, 'employee.photoUrl')) &&
                      <IconButton type="button" title="Limpar imagem" onClick={() => setFieldValue('employee.photo', null)}>
                        <MdDelete />
                      </IconButton>
                    }
                  </h4>
                  { (_.get(values, 'employee.photo') || _.get(values, 'employee.photoUrl')) ?
                    <ImageAuth withoutLoading={true} src={getPhotoUrl(values.person)} />
                    :
                    <FileImageCrop
                      label='Clique aqui para selecionar a foto'
                      name='employee.newPhoto'
                      aspect={1}
                      asBase64={true}
                      height={'200px'}
                      />
                  }
                </ImageContainer>
              </section>
              </>
            }
          </Container>
        </Modal>
      )}
    </Formik>
  );
};

export default EmployeeModal;
